import React from 'react';
import { Link } from '../ui/link';
import { useLocale } from '../../contexts/LocaleContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface LLMCall {
  id: string;
  userId: string | null;
  documentId: string | null;
  triggerSource: string;
  provider: 'gemini' | 'openai';
  model: string;
  prompt: string;
  response: string | null;
  promptTokens: number | null;
  responseTokens: number | null;
  costUsd: number | null;
  durationSeconds: number | null;
  createdAt: string;
}

interface Suggestion {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  originalText: string;
  suggestionText: string;
  issueType: 'orthographic' | 'preciseness' | 'clarity' | 'consistency' | 'orthographic_consistency' | 'grammar' | 'spelling' | 'punctuation' | 'sentence_structure' | 'repetition' | 'overused_words' | 'cacophony' | 'passive_voice' | 'show_vs_tell' | 'pacing' | 'adverbs' | 'adjectives' | 'cliches' | 'transitions' | 'style' | 'strength' | 'user_edit';
  feedbackText: string;
  llmCallId: string | null;
  llmCall?: LLMCall | null;
  languageCode: string | null;
  languageName: string | null;
  shownAt: string;
  status: 'pending' | 'applied' | 'rejected' | 'reported' | 'error:originalTextNotFound';
  closedAt: string | null;
  createdAt: string;
}

interface SuggestionDetailAdminModalProps {
  suggestion: Suggestion;
  onClose: () => void;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  showUserEmail?: boolean;
  getIssueTypeLabel: (type: string) => string;
  getStatusLabel: (status: string) => string;
}

const DetailBlock: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="mb-6">
    <div className="mb-2 text-sm font-medium text-muted-foreground">{label}</div>
    <div className="text-foreground">{children}</div>
  </div>
);

const PreBlock: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-sm ${className ?? ''}`}>
    {children}
  </div>
);

const issueBadgeClass = (issueType: string): string => {
  if (issueType === 'strength') {
    return 'border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200';
  }
  if (issueType === 'orthographic') {
    return 'border-transparent bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200';
  }
  return 'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200';
};

const statusBadgeClass = (status: string): string => {
  if (status === 'applied') {
    return 'border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200';
  }
  if (status === 'rejected' || status === 'error:originalTextNotFound') {
    return 'border-transparent bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200';
  }
  if (status === 'reported') {
    return 'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200';
  }
  return 'border-transparent bg-muted text-muted-foreground';
};

export const SuggestionDetailAdminModal: React.FC<SuggestionDetailAdminModalProps> = ({
  suggestion,
  onClose,
  onNavigatePrev,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
  showUserEmail = true,
  getIssueTypeLabel,
  getStatusLabel,
}) => {
  const { t } = useLocale();

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[900px]">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pr-8">
            {hasPrevious && onNavigatePrev && (
              <Button type="button" variant="outline" size="sm" onClick={onNavigatePrev}>
                ← {t('previous')}
              </Button>
            )}
            {hasNext && onNavigateNext && (
              <Button type="button" variant="outline" size="sm" onClick={onNavigateNext}>
                {t('next')} →
              </Button>
            )}
            <DialogTitle className="flex-1">{t('suggestion_details') || 'Suggestion details'}</DialogTitle>
          </div>
        </DialogHeader>

        {showUserEmail && (
          <DetailBlock label={t('user') || 'User'}>
            {suggestion.userEmail ? (
              <Link to={`/admin/users/${suggestion.userId}`} className="font-medium hover:underline">
                {suggestion.userEmail}
              </Link>
            ) : (
              '-'
            )}
          </DetailBlock>
        )}

        <DetailBlock label={t('original_text')}>
          <PreBlock>{suggestion.originalText}</PreBlock>
        </DetailBlock>

        <DetailBlock label={t('suggestion')}>
          <PreBlock>{suggestion.suggestionText}</PreBlock>
        </DetailBlock>

        <DetailBlock label={t('type') || 'Type'}>
          <Badge className={issueBadgeClass(suggestion.issueType)}>
            {getIssueTypeLabel(suggestion.issueType)}
          </Badge>
        </DetailBlock>

        <DetailBlock label={t('status') || 'Status'}>
          <Badge className={statusBadgeClass(suggestion.status)}>
            {getStatusLabel(suggestion.status)}
          </Badge>
        </DetailBlock>

        {suggestion.llmCall && (
          <>
            <DetailBlock label="LLM">{suggestion.llmCall.model}</DetailBlock>
            <DetailBlock label={t('provider') || 'Provider'}>
              <span className="capitalize">{suggestion.llmCall.provider}</span>
            </DetailBlock>
            {suggestion.llmCall.promptTokens !== null && (
              <DetailBlock label={t('input_tokens')}>{suggestion.llmCall.promptTokens.toLocaleString()}</DetailBlock>
            )}
            {suggestion.llmCall.responseTokens !== null && (
              <DetailBlock label={t('output_tokens')}>{suggestion.llmCall.responseTokens.toLocaleString()}</DetailBlock>
            )}
            {suggestion.llmCall.durationSeconds !== null && (
              <DetailBlock label={t('duration') || 'Duration'}>
                {suggestion.llmCall.durationSeconds.toFixed(2)}s
              </DetailBlock>
            )}
            {suggestion.llmCall.costUsd !== null && (
              <DetailBlock label={t('cost') || 'Cost'}>${suggestion.llmCall.costUsd.toFixed(8)}</DetailBlock>
            )}
            <DetailBlock label="Prompt">
              <PreBlock className="max-h-[200px] overflow-auto">{suggestion.llmCall.prompt}</PreBlock>
            </DetailBlock>
            {suggestion.llmCall.response && (
              <DetailBlock label="LLM">
                <PreBlock className="max-h-[300px] overflow-auto">{suggestion.llmCall.response}</PreBlock>
              </DetailBlock>
            )}
          </>
        )}

        {suggestion.feedbackText && (
          <DetailBlock label={t('user_feedback')}>
            <PreBlock>{suggestion.feedbackText}</PreBlock>
          </DetailBlock>
        )}

        <DetailBlock label={t('language') || 'Language'}>
          {suggestion.languageName || suggestion.languageCode || '-'}
        </DetailBlock>
        <DetailBlock label={t('shown') || 'Shown'}>
          {new Date(suggestion.shownAt).toLocaleString()}
        </DetailBlock>
        <DetailBlock label={t('created')}>
          {new Date(suggestion.createdAt).toLocaleString()}
        </DetailBlock>
        {suggestion.closedAt && (
          <DetailBlock label={t('closed') || 'Closed'}>
            {new Date(suggestion.closedAt).toLocaleString()}
          </DetailBlock>
        )}

        {suggestion.documentId && (
          <DialogFooter>
            <Button asChild>
              <Link to={`/documents/${suggestion.documentId}`}>{t('open_document') || 'Open document'}</Link>
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
